import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ocupacaoRedeService from '../services/ocupacaoRedeService';
import {
  applyOcupacaoFilters,
  buildOccupancyFilterLabel,
  normalizeCtoRow,
  sameOccupancyFilter,
} from '../utils/ocupacaoRedeAnalytics';

const OcupacaoRedeDataContext = createContext(null);

export const OcupacaoRedeDataProvider = ({ children }) => {
  const [rowsRaw, setRowsRaw] = useState([]);
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const response = await ocupacaoRedeService.buscarCtoPortas(controller.signal);

        if (!mounted) {
          return;
        }

        const rawRows = Array.isArray(response?.data?.cto_portas) ? response.data.cto_portas : [];
        setRowsRaw(rawRows.map((row, index) => normalizeCtoRow(row, index)));
      } catch (error) {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
          return;
        }

        if (!mounted) {
          return;
        }

        setRowsRaw([]);
        setErrorMessage(
          error.response?.data?.code === 'IXC_UNAVAILABLE'
            ? 'O IXC da empresa selecionada está indisponível no momento.'
            : 'Não foi possível carregar os dados de ocupação de rede agora.'
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [reloadKey]);

  const rowsFiltered = useMemo(
    () => applyOcupacaoFilters(rowsRaw, filters),
    [filters, rowsRaw],
  );

  const setFieldFilter = useCallback((field, value) => {
    setFilters((current) => {
      const withoutField = current.filter((filter) => filter.field !== field);

      if (value === null || value === undefined || value === '') {
        return withoutField;
      }

      return [
        ...withoutField,
        {
          field,
          value,
          label: buildOccupancyFilterLabel(field, value),
        },
      ];
    });
  }, []);

  const addFilter = useCallback((filter) => {
    setFilters((current) => (
      current.some((item) => sameOccupancyFilter(item, filter))
        ? current
        : [...current, filter]
    ));
  }, []);

  const removeFilter = useCallback((filterToRemove) => {
    setFilters((current) => current.filter((filter) => !sameOccupancyFilter(filter, filterToRemove)));
  }, []);

  const toggleFilter = useCallback((filterToToggle) => {
    setFilters((current) => (
      current.some((filter) => sameOccupancyFilter(filter, filterToToggle))
        ? current.filter((filter) => !sameOccupancyFilter(filter, filterToToggle))
        : [...current, filterToToggle]
    ));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters([]);
  }, []);

  const retry = useCallback(() => {
    setReloadKey((current) => current + 1);
  }, []);

  const value = useMemo(() => ({
    rowsRaw,
    rowsFiltered,
    filters,
    loading,
    errorMessage,
    setFieldFilter,
    addFilter,
    removeFilter,
    toggleFilter,
    clearFilters,
    retry,
  }), [
    clearFilters,
    errorMessage,
    filters,
    loading,
    removeFilter,
    retry,
    rowsFiltered,
    rowsRaw,
    setFieldFilter,
    addFilter,
    toggleFilter,
  ]);

  return (
    <OcupacaoRedeDataContext.Provider value={value}>
      {children}
    </OcupacaoRedeDataContext.Provider>
  );
};

export const useOcupacaoRedeData = () => {
  const context = useContext(OcupacaoRedeDataContext);

  if (!context) {
    throw new Error('useOcupacaoRedeData must be used within OcupacaoRedeDataProvider');
  }

  return context;
};

export default OcupacaoRedeDataContext;
